// client/src/hooks/useIntegracoes.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import useAlert from './useAlert';

const QUERY_KEY = 'integracoes';

// 🔹 Buscar a credencial configurada de um canal (whatsapp/email/sms).
// 404 significa "ainda não configurada" — não é um erro, retorna null.
export const useIntegracao = (canal) => {
    return useQuery({
        queryKey: [QUERY_KEY, canal],
        queryFn: async () => {
            try {
                const { data } = await api.get(`/integracoes/${canal}`);
                return data.data;
            } catch (error) {
                if (error.response?.status === 404) return null;
                throw error;
            }
        },
        enabled: !!canal,
    });
};

// 🔹 Salvar, remover e testar a credencial de um canal
export const useMutateIntegracao = (canal) => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidateCache = () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY, canal] });
    };

    const salvar = useMutation({
        mutationFn: async (payload) => {
            const { data } = await api.put(`/integracoes/${canal}`, payload);
            return data;
        },
        onSuccess: () => {
            invalidateCache();
            setAlert('Credencial salva com sucesso!', 'success');
        },
        onError: (err) => {
            setAlert(err.response?.data?.error || 'Erro ao salvar credencial', 'danger');
        },
    });

    const remover = useMutation({
        mutationFn: async () => {
            const { data } = await api.delete(`/integracoes/${canal}`);
            return data;
        },
        onSuccess: () => {
            invalidateCache();
            setAlert('Credencial desativada com sucesso!', 'success');
        },
        onError: (err) => {
            setAlert(err.response?.data?.error || 'Erro ao remover credencial', 'danger');
        },
    });

    // ✅ Não invalida cache nem alerta sozinho em sucesso — o resultado
    // (válido/inválido) é exibido inline no card pela própria tela.
    const testar = useMutation({
        mutationFn: async (campos) => {
            const { data } = await api.post(`/integracoes/${canal}/testar`, campos || {});
            return data.data;
        },
        onError: (err) => {
            setAlert(err.response?.data?.error || 'Erro ao testar credencial', 'danger');
        },
    });

    return { salvar, remover, testar };
};

// 🔹 Inicia (ou reinicia) o pareamento de WhatsApp via Evolution API — cria
// a instância no servidor self-hosted e devolve o QR Code inicial. Só faz
// sentido para o canal 'whatsapp', escopo Conta (backend restringe via
// authorize('conta')).
export const useConectarEvolution = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    return useMutation({
        mutationFn: async () => {
            const { data } = await api.post('/integracoes/whatsapp/evolution/conectar');
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'whatsapp'] });
        },
        onError: (err) => {
            setAlert(err.response?.data?.error || 'Erro ao iniciar conexão com o WhatsApp', 'danger');
        },
    });
};

// 🔹 Consulta o status atual do pareamento Evolution API — usado no
// polling do modal de QR Code até a conexão ser confirmada (`status`
// vira 'ativo'). `enabled` controla o polling: só faz sentido rodar
// enquanto o modal de QR Code estiver aberto. TanStack Query v5 removeu
// onSuccess/onError de useQuery — quem consome este hook (EvolutionQrCodeModal)
// é responsável por reagir à mudança de status via useEffect, inclusive
// invalidando useIntegracao('whatsapp') quando `status` vira 'ativo'.
export const useStatusEvolution = (enabled) => {
    return useQuery({
        queryKey: [QUERY_KEY, 'whatsapp', 'evolution', 'status'],
        queryFn: async () => {
            const { data } = await api.get('/integracoes/whatsapp/evolution/status');
            return data.data;
        },
        enabled,
        refetchInterval: (query) => (query.state.data?.status === 'ativo' ? false : 3000),
    });
};
